import type {
  IDriveFile,
  IFile,
} from '@picsart/cloud-storage/types/core/types/files/file';

import getMediaTypeFromString from '@/utils/generation/getMediaTypeFromString';
import parsePresetAttributes from '@/utils/file/parsePresetAttributes';

import type { ContentFile, ContentFileAttributes } from '@/types';

// Attribute keys we pick off Drive files. The submit side still rejects >1
// attribute (backend bug), so we stash the preset slug under `category` there;
// `userReaction` is written separately via PATCH, so it's safe to read here too.
const ATTRIBUTE_KEYS: ReadonlyArray<keyof ContentFileAttributes> = [
  'category',
  'userReaction',
];

const readFileAttributes = (
  attributes?: Record<string, unknown>
): ContentFileAttributes | undefined => {
  if (!attributes) return undefined;

  const result: Partial<Record<keyof ContentFileAttributes, string>> = {};
  for (const key of ATTRIBUTE_KEYS) {
    const value = attributes[key];
    if (typeof value === 'string' && value) {
      result[key] = value;
    }
  }
  return Object.keys(result).length
    ? (result as ContentFileAttributes)
    : undefined;
};

/**
 * One Drive file as the `ContentFile` the creations grid and lightbox read.
 *
 * Shared rather than private to the creations list because the `file_uid`
 * deeplink resolves a single file and must produce exactly the same shape — a
 * second mapping would drift, and the lightbox would render one kind of entry
 * differently depending on how the user arrived at it.
 */
export const driveFileToContentFile = (file: IFile): ContentFile => {
  const attributes = readFileAttributes(file.attributes);

  return {
    name: file.name || 'Untitled',
    uuid: file.uid,
    url: file.sourceUrl,
    preview: file.preview,
    type: getMediaTypeFromString(file.content?.resourceType?.toLowerCase()),
    attributes,
    // Regrouped from the attribute the submit wrote, so a multi-output run
    // still reads as one run after a reload. Absent for single-output files.
    runId: parsePresetAttributes(attributes?.category).run,
  };
};

export const mapDriveFilesToContentFiles = (
  files: IFile[] | null
): ContentFile[] => (files?.length ? files.map(driveFileToContentFile) : []);

/**
 * The same mapping for a file fetched by id.
 *
 * `getFileById` answers with `IDriveFile`, which is a different shape from the
 * `IFile` the listing returns — the fields carry the same meaning under
 * different names (`id`/`uid`, `contentLink`/`sourceUrl`, `thumbnail`/`preview`)
 * and there is no `sourceUrl` at all. Adapting here keeps that mismatch in one
 * place, so consumers hold a single `ContentFile` shape however the file
 * arrived.
 *
 * `taskHandler` reads only `name` off this response, which is why the
 * divergence went unnoticed until a caller needed the whole file.
 */
export const driveFileByIdToContentFile = (file: IDriveFile): ContentFile => ({
  name: file.name || 'Untitled',
  uuid: file.id,
  url: file.contentLink,
  preview: file.thumbnail,
  runId: parsePresetAttributes(readFileAttributes(file.attributes)?.category)
    .run,
  // `IDriveFile.content` types `resourceType` as optional, unlike the listing
  // shape — default so an untyped file still maps to an image rather than
  // failing to compile.
  type: getMediaTypeFromString(file.content?.resourceType?.toLowerCase() ?? ''),
  attributes: readFileAttributes(file.attributes),
});
